/* global Lenis */

/**
 * Checks whether motion should be reduced. The accessibility drawer's "Pausar animações" switch
 * (components/acessibilidade) marks `<html>` with `data-motion` and wins over the OS setting; without
 * it, falls back to `prefers-reduced-motion`. Used to skip JS-driven motion (canvas, parallax, Lenis)
 * that a CSS-only kill switch can't reach; listen to `acessibilidade:movimento` to react to changes.
 * @returns {boolean} True when motion should be reduced.
 */
function prefersReducedMotion() {
    const state = document.documentElement.dataset.motion;
    if (state) {
        return state === 'paused';
    }
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Calculates the outer height of an element (content + padding + margin).
 * @param {HTMLElement} el Element to measure.
 * @returns {number} Outer height in pixels.
 */
// eslint-disable-next-line no-unused-vars -- usada por components/topo/topo.js
function completeHeight(el) {
    const curStyle = el.currentStyle || window.getComputedStyle(el);
    let outerHeight = el.offsetHeight;
    outerHeight += parseInt(curStyle.marginTop);
    outerHeight += parseInt(curStyle.marginBottom);
    return outerHeight;
}

/**
 * Rolagem suave com Lenis (libs/lenis): interpola só roda do mouse e trackpad — toque e teclado
 * seguem nativos, e a rolagem continua sendo a do documento, então position: sticky, os
 * IntersectionObserver e os scrollTo nativos funcionam como antes. Modais e menu lateral ficam de
 * fora (rolam por conta própria). Desligada para quem pede menos movimento no sistema.
 * @returns {void}
 */
function initSmoothScroll() {
    if (typeof Lenis === 'undefined' || prefersReducedMotion() || window.lenis) { return; }

    window.lenis = new Lenis({
        autoRaf: true,
        lerp: 0.085,
        prevent: (node) => Boolean(node.closest && node.closest('.modal, .offcanvas')),
    });
}

initSmoothScroll();

/**
 * Aplica os estilos de blocos distantes do topo (rodapé, formulário de contato) que o PHP imprime
 * com `media="print"` e `data-deferred` (deferredStyle() em head.php) para não segurarem o primeiro
 * desenho. O navegador já baixa o arquivo; aqui ele só passa a valer para a tela.
 * @returns {void}
 */
function initDeferredStyles() {
    document.querySelectorAll('link[data-deferred]').forEach((link) => {
        link.media = 'all';
    });
}

initDeferredStyles();

/**
 * Liga e desliga a rolagem suave quando a pausa de animações muda na gaveta de acessibilidade.
 * @param {CustomEvent} event Evento `acessibilidade:movimento` (detail.pausado).
 * @returns {void}
 */
function toggleSmoothScroll(event) {
    if (event.detail.pausado) {
        if (window.lenis) {
            window.lenis.destroy();
            window.lenis = null;
        }
        return;
    }
    initSmoothScroll();
}

document.addEventListener('acessibilidade:movimento', toggleSmoothScroll);

/**
 * Animações de entrada: `[data-reveal]` anima o próprio elemento e `[data-reveal-stagger]` anima
 * os filhos em cascata. Só opacidade e translate (sem layout), cada elemento uma única vez — depois
 * ganha .reveal-done e volta às transições que já tinha (hover etc.). O que já está na tela ao
 * carregar aparece direto, sem piscar. Sem JS ou com movimento reduzido, nada fica escondido.
 * @returns {void}
 */
function initReveal() {
    const groups = document.querySelectorAll('[data-reveal-stagger]');
    const targets = [...document.querySelectorAll('[data-reveal]')];
    groups.forEach((group) => {
        [...group.children].forEach((child, i) => {
            child.style.setProperty('--reveal-delay', `${Math.min(i, 8) * 70}ms`);
            targets.push(child);
        });
    });
    if (!targets.length || prefersReducedMotion() || !('IntersectionObserver' in window)) { return; }

    const done = (el) => el.classList.add('reveal-done');
    targets.forEach((el) => {
        if (el.getBoundingClientRect().top < window.innerHeight) { done(el); }
    });
    document.documentElement.classList.add('has-reveal');

    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (!entry.isIntersecting) { return; }
            const el = entry.target;
            observer.unobserve(el);
            el.classList.add('is-revealed');
            // transitionend borbulha dos filhos: só vale o do próprio elemento.
            const onEnd = (event) => {
                if (event.target !== el || event.propertyName !== 'opacity') { return; }
                el.removeEventListener('transitionend', onEnd);
                done(el);
            };
            el.addEventListener('transitionend', onEnd);
        });
    }, { rootMargin: '0px 0px -10% 0px' });

    targets.forEach((el) => {
        if (!el.classList.contains('reveal-done')) { observer.observe(el); }
    });

    // Pausa ligada na gaveta de acessibilidade: tudo que ainda não entrou aparece de uma vez.
    document.addEventListener('acessibilidade:movimento', (event) => {
        if (!event.detail.pausado) { return; }
        observer.disconnect();
        targets.forEach(done);
        document.documentElement.classList.remove('has-reveal');
    });
}

initReveal();

const TRAFFIC_ORIGIN_KEY = 'brsis_origem';
const TRAFFIC_ORIGIN_TTL = 90 * 24 * 60 * 60 * 1000;
const TRAFFIC_ORIGIN_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid', 'ttclid', 'li_fat_id'];
let currentTrafficOrigin = null;

/**
 * Guarda de onde o visitante veio (UTMs, click ids de Ads como o `gclid` do Google Ads e o
 * referrer externo) no localStorage, para o formulário de contato repassar mesmo depois de o
 * visitante navegar pelo site. Vale o último toque não direto: navegação interna e acesso direto
 * mantêm a origem já guardada. Do referrer fica só origem + caminho.
 * @returns {void}
 */
function captureTrafficOrigin() {
    const params = new URLSearchParams(window.location.search);
    const touch = {};

    TRAFFIC_ORIGIN_PARAMS.forEach((name) => {
        const value = params.get(name);
        if (value) { touch[name] = value.slice(0, 200); }
    });

    try {
        const referrer = new URL(document.referrer);
        if (referrer.hostname !== window.location.hostname) {
            touch.referrer = referrer.origin + referrer.pathname;
        }
    } catch {
        // Sem referrer (acesso direto) ou referrer inválido.
    }

    if (!Object.keys(touch).length) { return; }

    touch.landing_page = window.location.pathname;
    touch.timestamp = Date.now();
    currentTrafficOrigin = touch;
    try {
        window.localStorage.setItem(TRAFFIC_ORIGIN_KEY, JSON.stringify(touch));
    } catch {
        // Storage bloqueado (aba anônima, dados do site bloqueados): fica só a origem desta página.
    }
}

/**
 * Origem do tráfego registrada por captureTrafficOrigin(): a desta página, se houver; senão a
 * guardada (se tiver menos de 90 dias).
 * @returns {Object<string, string|number>} Campos da origem (objeto vazio num acesso direto).
 */
// eslint-disable-next-line no-unused-vars
function getTrafficOrigin() {
    if (currentTrafficOrigin !== null) { return currentTrafficOrigin; }
    try {
        const stored = JSON.parse(window.localStorage.getItem(TRAFFIC_ORIGIN_KEY));
        if (stored && Date.now() - stored.timestamp < TRAFFIC_ORIGIN_TTL) { return stored; }
    } catch {
        // Storage bloqueado ou valor corrompido.
    }
    return {};
}

captureTrafficOrigin();

/**
 * Mensagem para quem abre o console (item 3.7 da revisão pré-lançamento): o `</BR>` da marca, um
 * "log" no mesmo tom do código do agro que roda na ilustração do topo (inicial.js) e um convite com
 * o comando `brsis.vagas()`, que imprime o banco de talentos e o LinkedIn. Os links saem do próprio
 * site (base href e rodapé), sem URL fixa nem nada interno. Uma vez por carregamento de página.
 * @returns {void}
 */
function initConsoleSignature() {
    const mark = 'background:#224253;color:#7BB975;font:700 20px/1.6 "IBM Plex Mono",monospace;padding:4px 12px';
    const title = 'font:700 13px "DM Sans",sans-serif;padding-left:8px';
    // $color-2-display: o verde da marca some no tema claro do DevTools; este passa nos dois.
    const tag = 'color:#60956B;font:700 12px "IBM Plex Mono",monospace';
    const text = 'font:12px "IBM Plex Mono",monospace';
    const call = 'font:700 13px "DM Sans",sans-serif';
    const command = 'background:#224253;color:#FFF;font:12px "IBM Plex Mono",monospace;padding:2px 6px';
    const log = [
        ['[sync] ', 'aviário 07 · campo sem sinal · 3 registros na fila'],
        ['[sync] ', 'conexão restabelecida · 3/3 enviados ✓'],
        ['[ia]   ', 'talhão 12 · ndvi 0,81 · irrigação recomendada'],
        ['[silo] ', 'carga de soja registrada · entrega confirmada ao produtor']
    ];

    window.brsis = {
        /**
         * Imprime os canais para quem quer trabalhar com a BRSIS.
         * @returns {string} Linha final do console, no lugar do "undefined".
         */
        vagas() {
            const linkedin = document.querySelector('#footer a[href*="linkedin.com"]');
            console.log('%cBanco de talentos%c ' + new URL('curriculo/', document.baseURI).href, tag, text);
            if (linkedin) {
                console.log('%cLinkedIn         %c ' + linkedin.href, tag, text);
            }
            return 'Até breve. </BR>';
        }
    };

    console.log('%c</BR>%cBRSIS · software sob medida desde 2001', mark, title);
    console.log(log.map(() => '%c%s%c%s').join('\n'), ...log.flatMap(([label, line]) => [tag, label, text, line]));
    console.log('%cAbriu o console? A gente também começaria por aqui.\n%cbrsis.vagas()%c para trabalhar com nós.', call, command, text);
}

initConsoleSignature();

/**
 * Faz o botão "Voltar" (`.go-back`, em components/title-bar) voltar uma página no histórico.
 * @returns {void}
 */
function initBackButtons() {
    document.querySelectorAll('.go-back').forEach((button) => {
        button.addEventListener('click', () => {
            history.back();
        });
    });
}

initBackButtons();

/**
 * Escuta, na página toda, palavras digitadas e o código Konami (↑ ↑ ↓ ↓ ← → ← → B A), para os
 * easter eggs. Não reage a quem digita em campo de formulário, com o painel de contato, a gaveta
 * ou um modal abertos, nem a teclas com Ctrl, Alt ou Meta.
 * @param {Object<string, function(): void>} handlers Palavra (minúscula, sem acento) → função a chamar quando for digitada; a chave `konami` responde ao código Konami.
 * @returns {void}
 */
// eslint-disable-next-line no-unused-vars -- usada por pages/inicial e pages/404
function listenSecretWords(handlers) {
    const words = Object.keys(handlers).filter((word) => word !== 'konami');
    const konami = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'b', 'a'];
    let typed = '';
    let step = 0;

    document.addEventListener('keydown', (event) => {
        if (event.ctrlKey || event.metaKey || event.altKey || event.target.closest('input, textarea, select, [contenteditable], .offcanvas.show, .modal.show')) { return; }

        if (handlers.konami) {
            const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;
            step = key === konami[step] ? step + 1 : Number(key === konami[0]);
            if (step === konami.length) {
                step = 0;
                handlers.konami();
            }
        }

        if (event.key.length === 1) {
            typed = (typed + event.key.toLowerCase()).slice(-16);
            words.forEach((word) => {
                if (typed.endsWith(word)) {
                    typed = '';
                    handlers[word]();
                }
            });
        }
    });
}
